import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { BrandMark } from "@bht/ui";
import { loadSession } from "../lib/session.js";
import { ThemeToggle } from "./ThemeToggle.js";

/**
 * Public nav destinations, in one place so the desktop row and the phone menu
 * cannot drift apart. Adding a page here puts it in both.
 */
const NAV_LINKS = [
  { to: "/free-practice-tests", label: "Tests" },
  { to: "/blog/", label: "Blog" },
  { to: "/patterns", label: "Patterns" },
  { to: "/faq", label: "FAQ" },
  // Privacy sits in the nav, not only the footer, because Google's OAuth brand
  // review fetches the site as a crawler and has to FIND the policy. A sibling
  // project was rejected repeatedly for an undetectable policy and only passed
  // once its links were in the main nav. Footer-only was not enough.
  { to: "/privacy", label: "Privacy" },
];

const navLinkStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--fg-1)",
  textDecoration: "none",
};

/**
 * Marketing shell: top brand bar + page content + footer.
 *
 * Used for public unauthenticated pages (landing, privacy, terms,
 * support). Dark by default to match the app's instrument aesthetic,
 * but with softer typography in the body so prospects don't feel like
 * they're inside the cockpit yet.
 */
export function MarketingShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "var(--bg-0)",
        color: "var(--fg-1)",
      }}
    >
      <MarketingHeader />
      <main style={{ flex: 1 }}>{children}</main>
      <MarketingFooter />
    </div>
  );
}

function MarketingHeader() {
  const signedIn = !!loadSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // Navigating with the menu open would otherwise leave it covering the page
  // it just took you to.
  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      menuButtonRef.current?.focus(); // Escape must not strand focus in a hidden panel
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || menuButtonRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [menuOpen]);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 24px",
        borderBottom: "1px solid var(--border-line)",
        background: "var(--bg-0)",
      }}
    >
      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "var(--fg-0)",
          textDecoration: "none",
        }}
      >
        <BrandMark size={24} />
        <span
          className="bht-wordmark-text"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--fg-0)",
          }}
        >
          BREAK·THE·TEST
        </span>
      </Link>
      <nav className="bht-marketing-nav" style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {/* Desktop: the links sit in the bar, where there is room for them. */}
        {/* No inline display here on purpose: an inline style outranks the
            stylesheet, so the media query below 640px could not hide this row
            and the header overflowed to 512px inside a 393px bar. */}
        <div className="bht-nav-desktop">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} style={navLinkStyle}>
              {l.label}
            </Link>
          ))}
          <ThemeToggle size={18} />
        </div>

        {/* Phones: the same links behind a menu. The bar previously fit three
            items only by hiding FAQ below 520px and the wordmark below 390px,
            which meant adding a fourth destination cost an existing one. */}
        <button
          ref={menuButtonRef}
          type="button"
          className="bht-nav-toggle"
          aria-expanded={menuOpen}
          aria-controls="bht-marketing-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square">
            {menuOpen ? (
              <>
                <line x1="4" y1="4" x2="16" y2="16" />
                <line x1="16" y1="4" x2="4" y2="16" />
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="17" y2="6" />
                <line x1="3" y1="10" x2="17" y2="10" />
                <line x1="3" y1="14" x2="17" y2="14" />
              </>
            )}
          </svg>
        </button>

        {signedIn ? (
          <Link
            to="/app/home"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--accent-amber)",
              textDecoration: "none",
              padding: "6px 14px",
              border: "1px solid var(--accent-amber)",
              borderRadius: 4,
            }}
          >
            Open dashboard
          </Link>
        ) : (
          <Link
            to="/signin"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--accent-amber)",
              textDecoration: "none",
              padding: "6px 14px",
              border: "1px solid var(--accent-amber)",
              borderRadius: 4,
            }}
          >
            Sign in
          </Link>
        )}
      </nav>

      {/* Always in the DOM, hidden rather than unmounted, so these links are in
          the prerendered HTML a crawler reads no matter which viewport the
          prerender ran at. The panel itself only ever renders on phones. */}
      <div id="bht-marketing-menu" ref={menuRef} className="bht-nav-panel" hidden={!menuOpen}>
        {NAV_LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="bht-nav-panel__link" style={navLinkStyle}>
            {l.label}
          </Link>
        ))}
        <div className="bht-nav-panel__theme">
          <span style={{ ...navLinkStyle, color: "var(--fg-2)" }}>Theme</span>
          <ThemeToggle size={18} />
        </div>
      </div>
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer
      style={{
        marginTop: 80,
        padding: "32px 24px 40px",
        borderTop: "1px solid var(--border-line)",
        background: "var(--bg-1)",
      }}
    >
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 32,
          marginBottom: 28,
        }}
        className="bht-footer-grid"
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <BrandMark size={20} />
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--fg-0)",
              }}
            >
              BREAK·THE·TEST
            </span>
          </div>
          <p style={{ color: "var(--fg-2)", fontSize: 13, lineHeight: 1.6 }}>
            Train pattern recognition for SAT, PSAT, ACT, AP, and Common App.
            Five-minute drills, no app store, instant on every visit.
          </p>
        </div>
        <div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-2)",
              marginBottom: 12,
            }}
          >
            More
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            <li><FooterLink to="/free-practice-tests">Free SAT practice tests</FooterLink></li>
            <li><FooterLink to="/free-act-practice-tests">Free enhanced ACT practice tests</FooterLink></li>
            <li><FooterLink to="/blog/">Blog: SAT &amp; ACT strategy</FooterLink></li>
            <li><FooterLink to="/patterns">Wrong-answer patterns explained</FooterLink></li>
            <li><FooterLink to="/exam-dates">SAT &amp; ACT dates and deadlines</FooterLink></li>
            <li><FooterLink to="/faq">How it works &amp; FAQ</FooterLink></li>
            <li><FooterLink to="/methodology">Methodology: how questions are checked</FooterLink></li>
            <li><FooterLink to="/whats-new">What's new</FooterLink></li>
            <li><FooterLink to="/privacy">Privacy policy</FooterLink></li>
            <li><FooterLink to="/terms">Terms of service</FooterLink></li>
            <li><FooterLink to="/support">Support &amp; data deletion</FooterLink></li>
          </ul>
        </div>
        <div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-2)",
              marginBottom: 12,
            }}
          >
            Contact
          </div>
          <a
            href="mailto:support@breakthetest.app"
            style={{
              color: "var(--accent-amber)",
              fontSize: 13,
              textDecoration: "none",
            }}
          >
            support@breakthetest.app
          </a>
        </div>
      </div>
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          paddingTop: 20,
          borderTop: "1px solid var(--border-line)",
          color: "var(--fg-2)",
          fontSize: 11,
          lineHeight: 1.6,
        }}
      >
        <p style={{ margin: 0 }}>
          SAT, AP, PSAT, and Bluebook are registered trademarks of the
          College Board. ACT is a registered trademark of ACT, Inc.
          Common App is a trademark of The Common Application, Inc.
          None of these organizations are affiliated with, or endorse,
          Break the Test.
        </p>
        <p style={{ margin: "10px 0 0" }}>
          &copy; {new Date().getFullYear()} Break the Test.
        </p>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        color: "var(--fg-1)",
        fontSize: 13,
        textDecoration: "none",
      }}
    >
      {children}
    </Link>
  );
}
